import { useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useCollection } from './useFirestore'

/**
 * Hook to get spells available to the current user.
 *
 * Access rules:
 * - Admin (isOwner) sees all spells (except hidden in non-admin views)
 * - Regular users see spells granted to their linked characters
 *   ONLY if the character is active (active !== false)
 *
 * @returns {{
 *   spells: Array,
 *   spellsByCharacter: Map<characterId, spell[]>,
 *   userCharacters: Array,
 *   loading: boolean
 * }}
 */
export function useUserSpells() {
  const { user, isOwner } = useAuth()
  const { data: allSpells, loading: spellsLoading } = useCollection('spells', { orderByField: 'name' })
  const { data: allCharacters, loading: charsLoading } = useCollection('characters')

  const loading = spellsLoading || charsLoading

  const result = useMemo(() => {
    if (loading) {
      return { spells: [], spellsByCharacter: new Map(), userCharacters: [] }
    }

    // Admin sees all spells (non-hidden for player views)
    if (isOwner) {
      const visibleSpells = allSpells.filter(s => !s.hidden)
      return {
        spells: visibleSpells,
        spellsByCharacter: new Map(),
        userCharacters: [],
      }
    }

    if (!user) {
      return { spells: [], spellsByCharacter: new Map(), userCharacters: [] }
    }

    // Get characters linked to this user that are ACTIVE
    const userActiveCharacters = allCharacters.filter(
      c => c.linkedUserId === user.uid && c.active !== false
    )
    const userActiveCharacterIds = new Set(userActiveCharacters.map(c => c.id))

    // Filter spells granted to any of user's active characters
    const accessibleSpells = allSpells.filter(
      spell =>
        !spell.hidden &&
        spell.grantedCharacterIds?.some(id => userActiveCharacterIds.has(id))
    )

    // Group spells by which character grants access
    const spellsByCharacter = new Map()
    for (const char of userActiveCharacters) {
      const charSpells = accessibleSpells.filter(
        spell => spell.grantedCharacterIds?.includes(char.id)
      )
      if (charSpells.length > 0) {
        spellsByCharacter.set(char.id, charSpells)
      }
    }

    return {
      spells: accessibleSpells,
      spellsByCharacter,
      userCharacters: userActiveCharacters,
    }
  }, [user, isOwner, allSpells, allCharacters, loading])

  return { ...result, loading }
}

/**
 * Hook to get spells for a specific character.
 *
 * @param {string} characterId
 * @returns {{ spells: Array, loading: boolean }}
 */
export function useCharacterSpells(characterId) {
  const { data: allSpells, loading: spellsLoading } = useCollection('spells', { orderByField: 'name' })

  const spells = useMemo(() => {
    if (spellsLoading || !characterId) return []

    return allSpells.filter(
      spell => !spell.hidden && spell.grantedCharacterIds?.includes(characterId)
    )
  }, [allSpells, characterId, spellsLoading])

  return { spells, loading: spellsLoading }
}
